<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useField, useForm } from 'vee-validate'
import { useQueryClient } from '@tanstack/vue-query'
import { validationSchema } from './types'
import type { Elemental, ElementalType } from '~~/src/types/elemental'
import type { List } from '~~/src/types/list'
import { createList, removePromptFromList, savePromptToList } from '~~/src/api/lists'
import { TanStackQueryIds } from '~~/src/composables/query/query-ids'

interface IProps {
  element: Elemental | null | undefined
  elementalType: ElementalType
  popupShown?: boolean
  liveSave?: boolean
}

interface IEmits {
  (event: 'updateSaved', e: number): void
  (event: 'listAdded', e: number): void
  (event: 'listRemoved', e: number): void
}

type StepController = 'save-to-list' | 'save-to-team' | 'create-list' | 'create-team'

const props = withDefaults(defineProps<IProps>(), { liveSave: true })
const emit = defineEmits<IEmits>()
const { element, popupShown, elementalType, liveSave } = toRefs(props)

const user = useUser()
const { colors } = useTheme()
const elementalLists = useElementalLists()
const tempSavedLists = useTemporarySavedLists()
const queryClient = useQueryClient()

const lists = ref<List[]>()

const { errors, handleSubmit } = useForm({
  validationSchema: toTypedSchema(validationSchema),
  initialValues: {
    title: '',
    isPrivate: false,
  },
})

const { value: newListTitle } = useField<string>('title')
const { value: newListIsPrivate } = useField<boolean>('isPrivate')
const newListError = ref<string>()

const stepController = ref<StepController>()

const isSaving = ref(false)
const loadingLists = ref(false)
const savingNewList = ref(false)

const savedToLists = ref<string[]>([])
const listSearch = ref<string>()

const popperRef = ref()
const showPopper = ref(false)

watch(elementalLists, () => {
  lists.value = elementalLists.value[element.value?.id || 'new-elemental']
}, { immediate: true })

const filteredLists = computed(() => lists.value?.filter((list) => {
  return listSearch.value
    ? list.title.toLowerCase().includes(listSearch.value?.toLowerCase() || '')
    : true
}))

const canAddToLists = computed(() => {
  const isOwner = element.value?.user?.id === user.value?.id
  return isOwner || !element.value?.isPremium
})

onClickOutside(popperRef, () => showPopper.value = false)

const saveUpdate = useUpdateElementalSave()
watch(saveUpdate, (update) => {
  if (element.value && update?.id === element.value.id)
    element.value.isSaved = update?.state === 'added'
})

const loadUserLists = async () => {
  if (popupShown.value && user.value && canAddToLists.value && !lists.value?.length) {
    loadingLists.value = true

    await loadElementalLists(element.value?.id || '', element.value?.isPremium, elementalType.value?.id)
    lists.value = elementalLists.value[element.value?.id || 'new-elemental']

    if (lists.value?.length) {
      savedToLists.value = lists.value.map(list => list.isPromptSaved ? list.id : '').filter(id => !!id)
      tempSavedLists.value[element.value?.id || 'new-elemental'] = savedToLists.value
    }

    loadingLists.value = false
  }
  else if (popupShown.value && lists.value?.length && !liveSave.value) {
    if (lists.value?.length)
      savedToLists.value = tempSavedLists.value[element.value?.id || 'new-elemental'] || []
  }
  else if (popupShown.value && lists.value?.length && liveSave.value) {
    savedToLists.value = tempSavedLists.value[element.value?.id || 'new-elemental'] || []
  }
}

watch(popupShown, loadUserLists, { immediate: true })

watch(stepController, async () => {
  if (stepController.value === 'create-list') {
    newListTitle.value = ''
    newListIsPrivate.value = false
  }
})

async function saveToList(list: List) {
  if (!liveSave.value) {
    const index = tempSavedLists.value[element.value?.id || 'new-elemental'].indexOf(list.id)
    if (index > -1)
      tempSavedLists.value[element.value?.id || 'new-elemental'].splice(index, 1)

    else
      tempSavedLists.value[element.value?.id || 'new-elemental'].push(list.id)

    savedToLists.value = tempSavedLists.value[element.value?.id || 'new-elemental']
    return
  }

  if (!element.value?.id)
    return

  const index = savedToLists.value.indexOf(list.id)
  if (index > -1) {
    savedToLists.value.splice(index, 1)
    await removePromptFromList(list.id, element.value?.id)
  }
  else {
    savedToLists.value.push(list.id)
    await savePromptToList(list.id, element.value?.id)
  }

  queryClient.refetchQueries({
    queryKey: ['profile-list-elementals'],
    exact: false,
    type: 'all',
  })

  queryClient.refetchQueries({
    queryKey: [TanStackQueryIds.LIST_ELEMENTALS],
    exact: false,
    type: 'all',
  })
}

const saveNewList = handleSubmit(async (values) => {
  newListError.value = undefined
  if (!newListTitle.value)
    return

  savingNewList.value = true

  const newList = await createList({
    title: values.title,
    isPrivate: values.isPrivate || false,
    description: '',
    itemIds: (element.value && liveSave.value)
      ? {
          listId: [],
          promptId: [element.value.id]
          ,
        }
      : {
          listId: [],
          promptId: [],
        },
    elementalTypeId: elementalType.value?.id,
  })

  if (newList?.id) {
    lists.value?.unshift(newList)
    savedToLists.value.push(newList.id)

    stepController.value = 'save-to-list'
  }
  else {
    newListError.value = 'Failed to create list'
  }

  savingNewList.value = false
})

async function handleSave() {
  if (isSaving.value || !element.value)
    return

  isSaving.value = true

  await handleElementalSave(element.value)
  queryClient.refetchQueries({
    queryKey: ['profile-list-elementals', elementalType.value.id],
    exact: false,
    type: 'all',
  })

  isSaving.value = false

  emit('updateSaved', element.value?.nSaves || 0)
  if (canAddToLists.value)
    showPopper.value = true
}

onMounted(() => {
  stepController.value = 'save-to-list'
  loadUserLists()
})
</script>

<script lang="ts">
export default defineComponent({
  name: 'ElementalSavePopup',
})
</script>

<template>
  <section v-if="element && liveSave" p-4 flex gap-3 justify-between border-b-1 border-soft-gray>
    <div flex gap-3 center>
      <img
        :src="element.avatar ? optimizeImage(element?.avatar, { width: 40, height: 40 })
          : '/prompt_avatar.png'"
        w-10 h-10
        border-1 border-soft-gray rounded-1
      >
      <div flex flex-col>
        <Text bold>
          Saved to
        </Text>
        <NuxtLink :to="user && user.username ? `/${user.username}` : undefined">
          <Text variant="subtitle" color-bluish-darker>
            All Saved {{ capitalizeFirstLetter(elementalType.name) }}s
          </Text>
        </NuxtLink>
      </div>
    </div>

    <div flex center text="20px">
      <button p-2 mr="[-8px]" @click.prevent.stop="handleSave()">
        <div v-if="element.isSaved" i-ri:bookmark-fill color-primary />
        <div v-else i-ri:bookmark-line />
      </button>
    </div>
  </section>

  <section v-if="!canAddToLists" p-4 flex-1 overflow-auto>
    <Text v-if="element?.isPrompt" :color="colors.bluishDarker" text-center whitespace-pre-wrap>
      You cannot include premium prompts from other users in your lists.
    </Text>
    <!-- list here is not used yet -->
    <Text v-else-if="element?.isList" :color="colors.bluishDarker" text-center whitespace-pre-wrap>
      You cannot include lists in other lists.
    </Text>
  </section>

  <section v-else-if="stepController === 'save-to-list'" p-4 flex-1 overflow-auto>
    <div flex flex-col gap-4>
      <div flex items-center justify-between>
        <Text variant="subtitle" :color="colors.bluishDarker">
          Add to Lists
        </Text>
        <div flex justify-between gap-3 items-center cursor-pointer text-soft-black class="!hover:text-bluish-darker" @click="stepController = 'create-list'">
          <Text variant="subtitle" bold color="inherit">
            New List
          </Text>

          <div i-mdi:plus-circle flex-shrink-0 text="20px" />
        </div>
      </div>

      <FormInput v-model="listSearch" placeholder="Search" class="!rounded-full">
        <template #prefix>
          <div i-mdi:magnify mr-2 text="20px" color-bluish-softer />
        </template>
      </FormInput>

      <div flex flex-col gap-4 overflow-auto>
        <template v-if="filteredLists && filteredLists.length">
          <div v-for="list in filteredLists" :key="list.id" py-1 flex gap-3 justify-between items-center cursor-pointer :title="list.title" @click="saveToList(list)">
            <Text :color="colors.softBlack" class="!hover:text-bluish-darker" :max-lines="1">
              {{ list.title }}
            </Text>

            <div v-if="savedToLists.includes(list.id)" i-mdi:check-circle flex-shrink-0 text-bluish-darker text="20px" />
            <div v-else i-mdi:circle-outline flex-shrink-0 text-bluish-softer text="20px" />
          </div>
        </template>
        <Text v-else-if="!loadingLists" :color="colors.bluishDarker" :max-lines="1">
          No lists found.
        </Text>
      </div>

      <div v-if="loadingLists" flex center>
        <div animate-spin :style="{ animationDuration: '2s' }">
          <IconSpinner :size="20" :color="colors.darkerGray" />
        </div>
      </div>
    </div>
  </section>

  <section v-else-if="stepController === 'create-list'" p-4>
    <form flex flex-col gap-4 @submit="saveNewList">
      <div flex justify-between items-center>
        <Text variant="subtitle" :color="colors.bluishDarker">
          New List
        </Text>

        <div i-mdi:close text-soft-black text="16px" cursor-pointer @click="stepController = 'save-to-list'" />
      </div>

      <FormInput v-model="newListTitle" :error="errors.title" placeholder="Enter list name" @keydown.enter.prevent.stop="saveNewList" />

      <Text v-if="newListError" text="red-500">
        {{ newListError }}
      </Text>

      <div flex justify-between>
        <FormToggle v-model="newListIsPrivate" :error="errors.isPrivate">
          <template #label>
            <Heading level="5" :color="colors.softBlack">
              Private List
            </Heading>
          </template>
        </FormToggle>

        <AppButton :enabled="!savingNewList" variant="outline" h-10 pl-4 pr-4 class="!border-soft-gray" type="submit" @click.prevent.stop="saveNewList">
          <Heading level="5" color-bluish-darker>
            Create
          </Heading>
        </AppButton>
      </div>
    </form>
  </section>
</template>
